import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  ColumnDef,
  flexRender,
  SortingState,
  ColumnFiltersState
} from '@tanstack/react-table';
import { getPivotLevelStats, PivotLevelStats } from '../api/pivotLevelStats';
import StandaloneSection from './shared/StandaloneSection';
import { useGlobalAutoRefreshStore, useAutoRefreshTrigger } from '../stores/useGlobalAutoRefreshStore';
import './PivotLevelReliabilityTable.css';

interface PivotLevelReliabilityTableProps {
  productId: string;
  lookbackDays?: number;
  selectedTimeframe?: string;
}

export function PivotLevelReliabilityTable({
  productId,
  lookbackDays = 30,
  selectedTimeframe
}: PivotLevelReliabilityTableProps) {
  const queryClient = useQueryClient();
  const setActiveDashboard = useGlobalAutoRefreshStore((state) => state.setActiveDashboard);
  const refreshTrigger = useAutoRefreshTrigger('market-overview');
  const [sorting, setSorting] = useState<SortingState>([{ id: 'successRate', desc: true }]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  // Automatically apply timeframe filter when selectedTimeframe changes
  useEffect(() => {
    if (selectedTimeframe) {
      setColumnFilters([{ id: 'timeframe', value: selectedTimeframe }]);
    } else {
      setColumnFilters([]);
    }
  }, [selectedTimeframe]);

  useEffect(() => {
    setActiveDashboard('market-overview');
    return () => setActiveDashboard(null);
  }, [setActiveDashboard]);

  useEffect(() => {
    if (refreshTrigger > 0) {
      queryClient.invalidateQueries({ queryKey: ['pivot-level-stats', productId, lookbackDays] });
    }
  }, [refreshTrigger, queryClient, productId, lookbackDays]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['pivot-level-stats', productId, lookbackDays],
    queryFn: () => getPivotLevelStats({ productId, lookbackDays }),
    retry: 2,
    refetchInterval: 120000, // Refresh every 2 minutes
  });

  const columns = useMemo<ColumnDef<PivotLevelStats>[]>(() => [
    {
      accessorKey: 'timeframe',
      header: 'Timeframe',
      cell: (info) => (
        <span className="timeframe-cell">{info.getValue() as string}</span>
      ),
      filterFn: 'equals',
    },
    {
      accessorKey: 'levelType',
      header: 'Level Type',
      cell: (info) => (
        <span className="level-type-cell">{info.getValue() as string}</span>
      ),
    },
    {
      accessorKey: 'totalInteractions',
      header: 'Total',
      cell: (info) => (
        <span className="total-cell">{info.getValue() as number}</span>
      ),
    },
    {
      accessorKey: 'touchCount',
      header: 'Touches',
      cell: (info) => (
        <span className="touch-cell">{info.getValue() as number}</span>
      ),
    },
    {
      accessorKey: 'bounceCount',
      header: 'Bounces (S)',
      cell: (info) => (
        <span className="bounce-cell">{info.getValue() as number}</span>
      ),
    },
    {
      accessorKey: 'rejectionCount',
      header: 'Rejections (R)',
      cell: (info) => (
        <span className="rejection-cell">{info.getValue() as number}</span>
      ),
    },
    {
      accessorKey: 'bullishBreakCount',
      header: 'Bullish Breaks',
      cell: (info) => (
        <span className="bullish-break-cell">{info.getValue() as number}</span>
      ),
    },
    {
      accessorKey: 'bearishBreakCount',
      header: 'Bearish Breaks',
      cell: (info) => (
        <span className="bearish-break-cell">{info.getValue() as number}</span>
      ),
    },
    {
      accessorKey: 'successRate',
      header: 'Success Rate',
      cell: (info) => {
        const rate = info.getValue() as number;
        const ratePct = rate * 100; // Convert from decimal (0.0-1.0) to percentage (0-100)
        let className = 'success-rate-cell';
        if (ratePct >= 70) className += ' high';
        else if (ratePct >= 50) className += ' medium';
        else className += ' low';

        return <span className={className}>{ratePct.toFixed(2)}%</span>;
      },
    },
    {
      accessorKey: 'reliabilityGrade',
      header: 'Grade',
      cell: (info) => {
        const grade = info.getValue() as string;
        let className = 'grade-badge';
        if (grade === 'A') className += ' grade-a';
        else if (grade === 'B') className += ' grade-b';
        else if (grade === 'C') className += ' grade-c';
        else if (grade === 'D') className += ' grade-d';
        else className += ' grade-f';

        return <span className={className}>{grade}</span>;
      },
    },
  ], []);

  const table = useReactTable({
    data: data?.stats || [],
    columns,
    state: {
      sorting,
      columnFilters,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  const getReliabilityGradeSummary = () => {
    if (!data?.stats) return null;

    const gradeCounts = data.stats.reduce((acc, stat) => {
      acc[stat.reliabilityGrade] = (acc[stat.reliabilityGrade] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return (
      <div className="grade-summary">
        {['A', 'B', 'C', 'D', 'F'].map(grade => (
          gradeCounts[grade] ? (
            <span key={grade} className={`grade-count grade-${grade.toLowerCase()}`}>
              {grade}: {gradeCounts[grade]}
            </span>
          ) : null
        ))}
      </div>
    );
  };

  if (isLoading) {
    return (
      <StandaloneSection title="PIVOT LEVEL RELIABILITY">
        <div className="loading-state">Loading reliability statistics...</div>
      </StandaloneSection>
    );
  }

  if (isError) {
    return (
      <StandaloneSection title="PIVOT LEVEL RELIABILITY">
        <div className="error-state">Error loading reliability statistics</div>
      </StandaloneSection>
    );
  }

  if (!data?.stats || data.stats.length === 0) {
    return (
      <StandaloneSection title="PIVOT LEVEL RELIABILITY">
        <div className="empty-state">
          No interaction data available for the selected period ({lookbackDays} days)
        </div>
      </StandaloneSection>
    );
  }

  return (
    <StandaloneSection
      title="PIVOT LEVEL RELIABILITY"
      actions={
        <div className="table-actions">
          {getReliabilityGradeSummary()}
          <span className="lookback-info">{lookbackDays} day lookback</span>
        </div>
      }
    >
      <div className="pivot-level-reliability-table">

        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              {table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map(header => (
                    <th
                      key={header.id}
                      onClick={header.column.getCanSort() ? header.column.getToggleSortingHandler() : undefined}
                      className={header.column.getCanSort() ? 'sortable' : ''}
                    >
                      <div className="header-content">
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {header.column.getIsSorted() && (
                          <span className="sort-indicator">
                            {header.column.getIsSorted() === 'desc' ? ' ▼' : ' ▲'}
                          </span>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map(row => (
                <tr key={row.id}>
                  {row.getVisibleCells().map(cell => (
                    <td key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="table-info">
          Showing {table.getRowModel().rows.length} of {data.stats.length} levels
        </div>
      </div>
    </StandaloneSection>
  );
}

